import { coreRootReducer } from '@liefersoft/pos-ui-core'
import { AnyAction } from 'redux'
import { SET_LOGIN_FAILED, SET_LOGOUT } from '../redux/actionTypes'
import { legacyRootReducer } from '../redux/reducer'

type LegacyState = ReturnType<typeof legacyRootReducer>
type CoreState = ReturnType<typeof coreRootReducer>
export type RootState = LegacyState & { core: CoreState }

const resolveToggled = (previousState: RootState, nextLegacyState: LegacyState, nextCoreState: CoreState): boolean => {
	const nextLegacyToggled = !!nextLegacyState?.system?.toggled
	const nextCoreToggled = !!nextCoreState?.system?.toggled

	if (nextLegacyToggled !== !!previousState?.system?.toggled) return nextLegacyToggled
	if (nextCoreToggled !== !!previousState?.core?.system?.toggled) return nextCoreToggled

	return nextLegacyToggled
}

const withToggled = <T extends { toggled?: boolean }>(systemState: T, toggled: boolean): T => {
	return systemState?.toggled === toggled ? systemState : { ...systemState, toggled }
}

const syncCoreStateWithLegacy = (coreState: CoreState, legacyState: LegacyState, actionType: string, toggled: boolean): CoreState => {
	return {
		...coreState,
		auth: actionType === SET_LOGIN_FAILED
			? {
				...coreState?.auth,
				accessToken: null,
				refreshToken: null,
			}
			: coreState?.auth,
		login: {
			...coreState?.login,
			email: legacyState?.login?.email ?? null,
			companyIndex: legacyState?.login?.companyIndex ?? null,
			invalidPasswordError: legacyState?.login?.invalidPasswordError ?? false,
			invalidIndexError: legacyState?.login?.invalidIndexError ?? false,
		},
		system: withToggled(coreState?.system, toggled),
	} as CoreState
}

export const rootReducer = (state: RootState | undefined, action: AnyAction): RootState => {
	const currentState = state ?? ({} as RootState)
	const { core, ...legacyStateRest } = currentState
	const legacyState = legacyStateRest as LegacyState

	const nextLegacyState = legacyRootReducer(legacyState, action)
	const nextCoreBaseState = action.type === SET_LOGOUT
		? coreRootReducer(undefined, action)
		: coreRootReducer(core, action)

	const toggled = resolveToggled(currentState, nextLegacyState, nextCoreBaseState)

	return {
		...nextLegacyState,
		system: withToggled(nextLegacyState.system, toggled),
		core: syncCoreStateWithLegacy(nextCoreBaseState, nextLegacyState, action.type, toggled),
	}
}
